<script setup lang="ts">
import { computed } from 'vue'
import { useDocumentsStore } from '../store'
import { DOCUMENT_REACTIONS } from '@/types'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { SmilePlus } from 'lucide-vue-next'

const props = defineProps<{
  documentId: string
  reactions?: Record<string, string[]>
  actorId: string
}>()

const documentsStore = useDocumentsStore()

const activeReactions = computed(() => {
  if (!props.reactions) return []
  return Object.entries(props.reactions)
    .filter(([, actors]) => actors.length > 0)
    .map(([emoji, actors]) => ({
      emoji,
      count: actors.length,
      hasReacted: props.actorId ? actors.includes(props.actorId) : false,
    }))
})

const handleToggle = (emoji: string) => {
  if (!props.actorId) return
  documentsStore.toggleReaction(props.documentId, emoji, props.actorId)
}
</script>

<template>
  <div class="flex items-center gap-1.5 flex-wrap">
    <!-- Active reaction pills -->
    <button
      v-for="reaction in activeReactions"
      :key="reaction.emoji"
      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors"
      :class="[
        reaction.hasReacted
          ? 'bg-primary/10 border-primary/30 text-primary'
          : 'bg-muted border-transparent text-muted-foreground',
        actorId ? 'hover:bg-muted/80 cursor-pointer' : 'cursor-default',
      ]"
      :disabled="!actorId"
      @click="handleToggle(reaction.emoji)"
    >
      <span>{{ reaction.emoji }}</span>
      <span>{{ reaction.count }}</span>
    </button>

    <!-- Add reaction button -->
    <Popover v-if="actorId">
      <PopoverTrigger as-child>
        <Button variant="ghost" size="icon" class="h-6 w-6 rounded-full">
          <SmilePlus class="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent class="w-auto p-2" align="start">
        <div class="flex items-center gap-1">
          <button
            v-for="emoji in DOCUMENT_REACTIONS"
            :key="emoji"
            class="h-8 w-8 flex items-center justify-center rounded hover:bg-muted transition-colors text-base"
            @click="handleToggle(emoji)"
          >
            {{ emoji }}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>
